import Controller from '@ember/controller';
import { type Registry as Services, service } from '@ember/service';
import type { HasMany } from '@ember-data/model';
import ENV from 'lumen/config/environment';
import type { LookerDashboard, LookerFolder } from 'lumen/models';
import type MarketCustomReportingRoute from 'lumen/routes/market/custom-reporting';
import type AjaxService from 'lumen/services/ajax';
import { isFolderOpen } from 'lumen/utils/custom-reporting/helpers';
import type { ModelFrom } from 'lumen/utils/routes';

export type CategoryType = 'personal' | 'portfolio' | 'templates';

export type SidebarCategory = {
  dashboards: HasMany<LookerDashboard> | undefined;
  folder: LookerFolder;
  folders: LookerFolder[] | HasMany<LookerFolder> | undefined;
  isOpen: boolean;
  title: string;
  type: CategoryType;
};

export default class MarketCustomReportingController extends Controller {
  declare model: ModelFrom<MarketCustomReportingRoute>;

  @service declare access: Services['access'];
  @service declare ajax: AjaxService;
  @service declare customReporting: Services['custom-reporting'];
  @service declare intl: Services['intl'];

  get categories(): SidebarCategory[] {
    if (this.model.folders.isRunning) {
      return [];
    }
    const result = [];
    const { model } = this;
    const { selectedDashboard } = this.customReporting;
    const folders = model.folders.lastSuccessful?.value;
    const personal = folders?.find((folder) => folder.isPersonal);
    const templates = folders?.find((folder) => folder.isSharedRoot);
    const portfolios = folders?.find((folder) => folder.isEmbedSharedRoot);

    const myFolder = {
      dashboards: personal?.dashboards,
      folders: personal?.folders.filter((f) => !f.isNew),
    };

    const templateFolder = {
      dashboards: templates?.dashboards,
      folders: templates?.folders.filter((f) => !f.isNew),
    };

    if (this.access.hasCustomReportingEdit && personal) {
      const isOpen = selectedDashboard
        ? isFolderOpen(personal, selectedDashboard)
        : false;

      result.push({
        title: this.intl.t('custom_reporting.my_dashboards'),
        dashboards: myFolder.dashboards,
        isOpen,
        folders: myFolder.folders,
        folder: personal,
        type: 'personal' as const,
      });
    }

    if (portfolios && [...portfolios.folders].length) {
      // if there's only a single folder under portfolios, we need to move it up
      const hasMultipleFolders = [...portfolios.folders].length > 1;

      const portfoliosFolder = hasMultipleFolders
        ? portfolios
        : (portfolios.folders[0] as LookerFolder);

      const isOpen = selectedDashboard
        ? isFolderOpen(portfoliosFolder, selectedDashboard)
        : false;

      result.push({
        title: hasMultipleFolders
          ? this.intl.t('custom_reporting.portfolios')
          : this.intl.t('custom_reporting.portfolio'),
        dashboards: portfoliosFolder.dashboards,
        isOpen,
        folders: portfoliosFolder.folders,
        folder: portfoliosFolder,
        type: 'portfolio' as const,
      });
    }

    if (templates) {
      result.push({
        title: this.intl.t('custom_reporting.made_for_you'),
        dashboards: templateFolder.dashboards,
        isOpen: true,
        folders: templateFolder.folders,
        folder: templates,
        type: 'templates' as const,
      });
    }

    return result;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get embedDomain() {
    return ENV.isDevelopmentOrTest ? 'http://localhost:4200' : this.ajax.host;
  }
}
